<template>
  <div class="nlp-settings">
    <section-title>{{ $t('plugins.nlp.settings.sectionTitle') }}</section-title>
    <section-title subsection>Local states</section-title>
    <div class="nlp-settings__section editor-section">
      <div class="editor-section-description">Define states by set of rules.</div>
      <div class="nlp-settings__states">
        <div class="nlp-settings__states-item"
          v-for="(item, index) in actStates"
          :key="index">
          <st-builder
            class="nlp-settings__st-builder" 
            v-model="states[index]"
            @delete="deleteState(index)"/>
        </div>
        <st-builder
          class="nlp-settings__st-builder"
          :noDelete="true"
          v-model="newState"
          @nameBlur="addNewState($event)"/>
      </div>
      <btn
        class="nlp-settings__basic-section-button"
        icon="save"
        @click="saveEditedStates"
      >{{ $t('btn.save') }}</btn>
    </div>
    <section-title subsection>{{ $t('plugins.nlp.settings.fuzzy.title') }}</section-title>
    <div class="nlp-settings__section editor-section">
      <div class="editor-section-description">{{ $t('plugins.nlp.settings.fuzzy.descParams') }}</div>
      <div class="nlp-settings__fuzzy-params-cont">
        <in
          class="nlp-settings__input nlp-settings__input"
          :label="$t('plugins.nlp.settings.fuzzy.ins')"
          v-model="fuzzySettings.ins"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__input"
          :label="$t('plugins.nlp.settings.fuzzy.del')"
          v-model="fuzzySettings.del"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__input--lower"
          :label="$t('plugins.nlp.settings.fuzzy.sub')"
          v-model="fuzzySettings.sub"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__input--lower"
          :label="$t('plugins.nlp.settings.fuzzy.subSpecial')"
          v-model="fuzzySettings.subSpecial"
          type="number"
          min="0">
        </in>
      </div>
      <div class="editor-section-description nlp-settings__editor-section-margin">{{ $t('plugins.nlp.settings.fuzzy.descDistance') }}</div>
      <in
        class="nlp-settings__input nlp-settings__fuzzy-input-threshold"
        :label="$t('plugins.nlp.settings.fuzzy.threshold')"
        v-model="fuzzySettings.threshold"
        type="number"
        min="0">
      </in>
      <div class="editor-section-description nlp-settings__editor-section-margin">Special distance thresholds for phrase lengths.</div>
      <div v-for="(item, index) in fuzzySettings.thresholdSpecial"
        :key="index"
        class="nlp-settings__fuzzy-item-special-threshold">
        <in
          class="nlp-settings__input nlp-settings__fuzzy-input-special-threshold"
          label="length"
          v-model="item.len"
          type="number"
          min="1">
        </in>
        <in
          class="nlp-settings__input nlp-settings__fuzzy-input-special-threshold"
          :label="$t('plugins.nlp.settings.fuzzy.threshold')"
          v-model="item.threshold"
          placeholder="0"
          type="number"
          min="0">
        </in>
        <fai icon="ban" 
          class="nlp-settings__fuzzy-remove-threshold-icon"
          @click="removeThreshold(index)"/>
      </div>
      <div class="nlp-settings__fuzzy-add-threshold-cont">
        <div
          @click="addNewThreshold"
          class="nlp-settings__fuzzy-add-threshold-button"
          >add new range</div>
      </div>
      <btn
        class="nlp-settings__basic-section-button"
        icon="save"
        @click="saveFuzzySettings"
      >{{ $t('btn.save') }}</btn>
    </div>
    <section-title subsection>{{ $t('plugins.nlp.settings.train.title') }}</section-title>
    <div class="editor-section-description">{{ $t('plugins.nlp.settings.train.buttonSub') }}</div>
    <div class="nlp-settings__section editor-section">
      <btn
        class="nlp-settings__train-btn"
        icon="sync-alt"
        color="red"
        @click="trainNetwork()"
      >{{ $t('plugins.nlp.settings.train.buttonText') }}</btn>
    </div>
    <div class="editor-section-description">Network training parameters.</div>
    <div class="nlp-settings__section editor-section nlp-settings__training-section">
      <div class="nlp-settings__training-cont">
        <in
          class="nlp-settings__input nlp-settings__training-input"
          label="iterations"
          v-model="trainingSettings.iterations"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__training-input"
          label="error threshold"
          v-model="trainingSettings.errorThresh"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__training-input"
          label="learning rate"
          v-model="trainingSettings.learningRate"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__training-input"
          label="momentum"
          v-model="trainingSettings.momentum"
          type="number"
          min="0">
        </in>
        <in
          class="nlp-settings__input nlp-settings__training-input"
          label="timeout"
          v-model="trainingSettings.timeout"
          type="number"
          min="0">
        </in>
      </div>
    </div>
    <div class="editor-section-description">
      Choose from which file network will be restored. If no file is selected, network will be trained with actual state of model.
    </div>
    <div class="nlp-settings__section editor-section nlp-settings__training-section">
      <sel class="nlp-settings__training-restore-sel"
        label="Restore from"
        :value="trainingSettings.restore_from"
        @input="trainingSettings.restore_from = $event">
        <option :value="null">
          -
        </option>
        <option
          v-for="(item, index) in networks"
          :key="index"
          :value="item.id"
        >{{ item.name }}</option>
      </sel>
      <btn
        class="nlp-settings__basic-section-button"
        icon="save"
        @click="saveTrainingSettings"
      >{{ $t('btn.save') }}</btn>
    </div>
    <section-title subsection>{{ $t('plugins.nlp.settings.savedNetworks.title') }}</section-title>
    <div class="nlp-settings__section editor-section">
      <div class="editor-section-description">{{ $t('plugins.nlp.settings.savedNetworks.export.desc') }}</div>
      <div class="nlp-settings__networks-exp">
        <div class="nlp-settings__networks-exp-inputs">
          <sel class="nlp-settings__networks-exp-sel"
            :label="$t('plugins.nlp.settings.savedNetworks.export.sel')"
            @input="exportSelInput($event)"
            :value="getValueForSel">
            <option :value="-1">
              {{ $t('plugins.nlp.settings.savedNetworks.export.selCreate') }}
            </option>
            <option
              v-for="(item, index) in networks"
              :key="index"
              :value="index"
            >{{ item.name }}</option>
          </sel>
          <in class="nlp-settings__networks-exp-in" v-if="networksExport.create"
            :placeholder="$t('plugins.nlp.settings.savedNetworks.export.new')"
            v-model="networksExport.newFile"
            @input="validation.network.filenameCreate = validateFilename(networksExport.newFile)"/>
          <btn class="nlp-settings__networks-exp-btn"
            icon="file-import"
            @click="exportNetwork()">
            {{ $t('plugins.nlp.settings.savedNetworks.export.btn') }}
          </btn>
        </div>
        <div class="nlp-settings__warning-text"
          v-if="!validation.network.filenameCreate && networksExport.create">
          {{ $t('plugins.nlp.validation.savedNetworks.filename.formatDesc') }}
        </div>
      </div>
    </div>
    <div class="nlp-settings__section editor-section">
      <div class="editor-section-description">{{ $t('plugins.nlp.settings.savedNetworks.import.desc') }}</div>
      <div class="nlp-settings__networks-imp">
        <sel class="nlp-settings__networks-exp-sel"
          :label="$t('plugins.nlp.settings.savedNetworks.import.sel')"
          v-model="networksImportSelected">
          <option :value="null">
            -
          </option>
          <option
            v-for="(item, index) in networks"
            :key="index"
            :value="index"
          >{{ item.name }}</option>
        </sel>
        <btn class="nlp-settings__networks-exp-btn"
          icon="file-export"
          @click="importNetwork()">
          {{ $t('plugins.nlp.settings.savedNetworks.import.btn') }}
        </btn>
      </div>
    </div>
    <div class="nlp-settings__section editor-section">
      <div class="editor-section-description">{{ $t('plugins.nlp.settings.savedNetworks.upload.desc') }}</div>
      <div class="nlp-settings__networks-upload">
        <input type="file"
          ref="refUploadInput"
          class="nlp-settings__networks-upload-input"
          @change="onFileChange($event.target.files)"
          @click="$event.target.value=null"
          name="network"
          accept="application/JSON"/>
        <btn class="nlp-settings__networks-upload-btn"
          icon="file-export"
          @click="$refs.refUploadInput.click()">
          {{ $t('plugins.nlp.settings.savedNetworks.upload.btn') }}
        </btn>
        <div class="nlp-settings__warning-text"
          v-if="!validation.network.filenameUpload">
          {{ $t('plugins.nlp.validation.savedNetworks.filename.formatDesc') }}
        </div>
      </div>
    </div>
    <div class="nlp-settings__section editor-section">
      <div class="editor-section-description">{{ $t('plugins.nlp.settings.savedNetworks.delete.desc') }}</div>
      <div class="nlp-settings__networks-imp">
        <sel class="nlp-settings__networks-exp-sel"
          :label="$t('plugins.nlp.settings.savedNetworks.delete.btn')"
          v-model="networksToDelete">
          <option :value="null">
            -
          </option>
          <option
            v-for="(item, index) in networks"
            :key="index"
            :value="index"
          >{{ item.name }}</option>
        </sel>
        <btn class="nlp-settings__networks-exp-btn"
          icon="trash"
          @click="deleteNetwork()"
          color="red">
          {{ $t('plugins.nlp.settings.savedNetworks.delete.btn') }}
        </btn>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
import dynText from './DynamicTextarea.vue'
import stBuilder from './StateBuilder.vue'

export default {
  data () {
    return {
      fuzzySettings: {
        ins: null,
        del: null,
        sub: null,
        subSpecial: null,
        threshold: null,
        thresholdSpecial: []
      },
      actStates: [],
      newState: null,
      statesToSave: {
        toDelete: []
      },
      trainingSettings: {
        iterations: null,
        errorThresh: null,
        restore_from: null
      },
      networks: [],
      networksExport: {
        newFile: '',
        file: null,
        create: true
      },
      networksImportSelected: null,
      networksToDelete: null,
      networkFile: null,
      validation: {
        network: {
          filenameCreate: true,
          filenameUpload: true
        }
      }
    }
  },

  props: {
    states: Array
  },

  watch: {
    states () {
      if(this.states) this.actStates = this.states
    }
  },

  mounted () {
    if(this.states) this.actStates = this.states
    this.fetchFuzzySettings()
    this.fetchSavedNetworks().then(this.fetchTrainingSettings())
  },

  computed: {
    getValueForSel () {
      if(this.networksExport.file === null) return -1
      for(let i = 0; i<this.networks.length; i++) if(this.networks[i] === this.networksExport.file) return i
      return null
    }
  },

  methods: {
    validateFilename (filename) {
      if(!filename || filename.length<4 || filename.length>32) return false
      const reg = new RegExp(/^([\w-])+$/)
      if(reg.test(filename)) return true
      return false
    },

    async deleteNetwork () {
      try {
        if(this.networksToDelete === null) return
        const network = this.networks[this.networksToDelete]
        await axios.post('/api/plugin/nlp/settings/saved-networks/delete', {
          id: network.id
        })
        this.notify('success', 'plugins.nlp.success.settings.savedNetworks.edit')
        this.networks.splice(this.networksToDelete, 1)
        this.networksToDelete = null
        this.networksImportSelected = null
        if(this.trainingSettings.restore_from == network.id) this.trainingSettings.restore_from = null
        if(this.networks.length === 0) this.networksExport.create = true
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.settings.savedNetworks.edit')
        console.error(e)
      }
    },

    getNetworkById (id) {
      for(let i = 0; i<this.networks.length; i++) {
        if(this.networks[i].id == id) {
          return this.networks[i]
        }
      }
      return null
    },

    async onFileChange (files) {
      try {
        const fileToUpload = files[0]

        if(!this.validateFilename(fileToUpload.name.split('.').slice(0, -1).join('.'))) {
          this.validation.network.filenameUpload = false
          return
        }
        this.validation.network.filenameUpload = true

        const formData = new FormData();
        formData.append('network', fileToUpload)

        const res = await axios.post('/api/plugin/nlp/settings/saved-networks/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        this.notify('success', 'plugins.nlp.success.settings.savedNetworks.edit')

        const name = fileToUpload.name.split('.').slice(0, -1).join('.')
        const newElem = {
          id: res.data.id,
          name
        }
        this.networks.push(newElem)
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.settings.savedNetworks.edit')
        console.error(e)
      } 
    },

    async importNetwork () {
      try {
        if(this.networksImportSelected === null) return
        const network = this.networks[this.networksImportSelected]
        await axios.post('/api/plugin/nlp/settings/saved-networks/import', {
          id: network.id
        })
        this.notify('success', 'plugins.nlp.success.settings.savedNetworks.edit')
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.settings.savedNetworks.edit')
        console.error(e)
      }
    },

    async exportNetwork () {
      try {
        const toSend = {
        }
        if(this.networksExport.create) {
          if(!this.validateFilename(this.networksExport.newFile)) return
          toSend.filename = this.networksExport.newFile
        } else {
          toSend.id = this.networksExport.file.id
        }
        const res = await axios.post('/api/plugin/nlp/settings/saved-networks/export', toSend)
        this.notify('success', 'plugins.nlp.success.settings.savedNetworks.edit')
        if(this.networksExport.create) this.networks.push({
          id: res.data.id,
          name: this.networksExport.newFile
        })
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.settings.savedNetworks.edit')
        console.error(e)
      }
    },

    exportSelInput (value) {
      if(value === '-1') {
        this.networksExport.create = true
        this.networksExport.file = null
      }
      else {
        this.networksExport.create = false
        this.networksExport.file = this.networks[parseInt(value)]
      }
    },

    async saveEditedStates () {
      try {
        const copyStates = JSON.parse(JSON.stringify(this.actStates))
        const crudTree = {
          toUpdate: [],
          toCreate: [],
          toDelete: this.statesToSave.toDelete
        }

        for(let i = 0; i<copyStates.length; i++) {
          if(copyStates[i].id !== undefined) {
            if(copyStates[i].edit) {
              delete copyStates[i].edit
              crudTree.toUpdate.push(copyStates[i])
            }
          } else {
            crudTree.toCreate.push(copyStates[i])
          }
        }

        const res = await axios.post('/api/plugin/nlp/settings/states', {
          states: crudTree
        })

        const createdIds = res.data.createdIds

        for(let i = 0; i<crudTree.toCreate.length; i++) {
          crudTree.toCreate[i].id = createdIds[i]
        }

        this.$emit('statesUpdate', copyStates)
      } catch (e) {
        console.error(e)
      }
    },

    addNewState (state) {
      if(state.name === '') return
      this.actStates.push(JSON.parse(JSON.stringify(state)))
      this.newState = {
        name: '',
        rule: {
          root: []
        }
      }
    },

    deleteState (index) {
      const state = this.actStates[index]
      if(state.id) this.statesToSave.toDelete.push(state.id)
      this.actStates.splice(index, 1)
    },

    addNewThreshold() {
      this.fuzzySettings.thresholdSpecial.push({
        len: 1,
        threshold: 2
      })
    },

    removeThreshold(index) {
      this.fuzzySettings.thresholdSpecial.splice(index, 1)
    },

    fetchFuzzySettings: async function () {
      try {
        const res = await axios.get('/api/plugin/nlp/settings/fuzzy/get')

        this.fuzzySettings = res.data.settings
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.main.load')
        console.error(e)
      }
    },

    fetchTrainingSettings: async function () {
      try {
        const res = await axios.get('/api/plugin/nlp/settings/training')

        this.trainingSettings = res.data.settings
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.main.load')
        console.error(e)
      }
    },

    fetchSavedNetworks: async function () {
      try {
        const res = await axios.get('/api/plugin/nlp/settings/saved-networks')

        this.networks = res.data.networks
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.main.load')
        console.error(e)
      }
    },

    saveFuzzySettings: async function () {
      try {
        if(!this.validateFuzzySettings()) {
          this.notify('error', 'plugins.nlp.validation.settings.fuzzy.format')
          return
        }

        for(var key of Object.keys(this.fuzzySettings)) {
          if(this.fuzzySettings[key] === Object(this.fuzzySettings[key])) continue
          this.fuzzySettings[key] = parseFloat(this.fuzzySettings[key])
        }

        for(let i = 0; i<this.fuzzySettings.thresholdSpecial.length; i++) {
          const act = this.fuzzySettings.thresholdSpecial[i]
          act.len = parseFloat(act.len)
          act.threshold = parseFloat(act.threshold)
        }
        console.log(this.fuzzySettings)
        
        const res = await axios.patch('/api/plugin/nlp/settings/fuzzy/update', {
          settings: this.fuzzySettings
        })

        this.notify('success', 'plugins.nlp.success.settings.fuzzy.edit')
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.settings.fuzzy.edit')
        console.error(e)
      }
    },

    async saveTrainingSettings () {
      try {
        if(!this.validateTrainingSettings()){
          this.notify('error', 'plugins.nlp.validation.settings.fuzzy.format')
          return
        }
        this.trainingSettings.iterations = parseInt(this.trainingSettings.iterations)
        this.trainingSettings.errorThresh = parseFloat(this.trainingSettings.errorThresh)
        this.trainingSettings.learningRate = parseFloat(this.trainingSettings.learningRate)
        this.trainingSettings.momentum = parseFloat(this.trainingSettings.momentum)
        this.trainingSettings.timeout = parseInt(this.trainingSettings.timeout)
        if(this.trainingSettings.restore_from === '') this.trainingSettings.restore_from = null

        await axios.patch('/api/plugin/nlp/settings/training', {
          settings: this.trainingSettings
        })
        this.notify('success', 'plugins.nlp.success.settings.training.edit')
      } catch (e) {
        this.notify('error', 'plugins.nlp.error.settings.training.edit')
        console.error(e)
      }
    },

    async trainNetwork () {
      try {
        await axios.get('/api/plugin/nlp/net/train')
      } catch (e) {
        console.error(e)
      }
    },

    validateFuzzySettings: function () {
      for(var key of Object.keys(this.fuzzySettings)) {
        if(this.fuzzySettings[key] === Object(this.fuzzySettings[key])) continue
        const value = this.fuzzySettings[key]
        if(!value || parseFloat(value)<=0) return false
      }
      return true
    },

    validateTrainingSettings () {
      if(!this.trainingSettings.iterations || parseInt(this.trainingSettings.iterations)<=0) return false
      if(!this.trainingSettings.errorThresh || parseFloat(this.trainingSettings.errorThresh)<=0 || parseFloat(this.trainingSettings.errorThresh)>1) return false
      if(!this.trainingSettings.learningRate || parseFloat(this.trainingSettings.learningRate)<=0 || parseFloat(this.trainingSettings.learningRate)>1) return false
      if(!this.trainingSettings.momentum || parseFloat(this.trainingSettings.momentum)<=0 || parseFloat(this.trainingSettings.momentum)>1) return false
      if(!this.trainingSettings.timeout === undefined || parseInt(this.trainingSettings.timeout)<0) return false
      return true
    }
  }, 

  components: {
    'dynamic-text': dynText,
    'st-builder': stBuilder
  }
}
</script>

<style lang="scss">
.nlp-settings {
  padding-left: 25px;
  padding-top: 6px;
  display: flex;
  flex-direction: column;
  max-height: 100%;

  &__section {
    margin-bottom: 40px;
    position: relative;
    width: 50%;
    min-width: 540px;
  }

  &__editor-section-margin {
    margin-top: 24px;
  }

  &__basic-section-button {
    position: absolute;
    top: calc(100% + 18px);
    right: 0;
  }

  &__states {
    width: 65%;
    max-height: 400px;
    padding-right: 10px;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__states-item {
  }

  &__fuzzy-params-cont {
    display: flex;
    flex-wrap: wrap;
  }

  &__input {
    width: 30%;
    margin-right: 4%;
    margin-bottom: 4px;

    &--lower {
      align-self: flex-end;
    }
  }

  &__fuzzy-item-special-threshold {
    display: flex;
    align-items: center;
  }

  &__fuzzy-add-threshold-cont {
    display: flex;
  }

  &__fuzzy-add-threshold-button {
    @include clickableText();
    //margin: 0;
  }

  &__fuzzy-remove-threshold-icon {
    cursor: pointer;
  }

  &__training-cont {
    display: flex;
    flex-wrap: wrap;
  }

  &__training-restore-sel {
    width: 160px;
  }

  &__networks-exp-inputs {
    margin-top: 8px;
    display: flex;
    align-items: flex-end;
  }

  &__networks-exp-sel {
    width: 160px;
    margin-right: 4%;
  }

  &__networks-exp-in {
    width: 160px;
    margin-right: 4%;
  }

  &__networks-exp-btn {
    margin-top: 16px;
  }

  &__networks-imp {
    display: flex;
  }

  &__networks-upload-input {
    display: none;
  }

  &__warning-text {
    position: absolute;
    margin-top: 10px;
    font-size: 0.8em;
    color: $danger;
    font-style: italic;
    font-weight: bold;
  }
}
</style>
